import { useState } from 'react';
import { ImageSearch } from '@mui/icons-material';
import { BaseModal } from '@/Components/Modals/BaseModal';
import { notificationServices } from '@/Services';
import productImageSuggestServices from '@/Services/productImageSuggest.services';

// "Suggest images" for the product form: finds similarly named products in our database (any
// customer, own products first) and offers the images configured on them. The picked one is
// handed to onPick as a File, which goes through the same crop dialog as an uploaded image and
// is saved as a new image for this product.
const dataUrlToFile = (dataUrl, name) => {
	const [meta, b64] = dataUrl.split(',');
	const type = meta.match(/data:([^;]+)/)?.[1] || 'image/jpeg';
	const bytes = atob(b64);
	const arr = new Uint8Array(bytes.length);
	for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
	return new File([arr], name, {
		type,
	});
};

const EMPTY_RESULT = {
	items: [],
	page: 0,
	pageCount: 0,
};

const ProductImageSuggestions = ({ productName = '', onPick, isDisabled = false }) => {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');
	const [result, setResult] = useState(EMPTY_RESULT);
	const [loading, setLoading] = useState(false);
	const [pickingId, setPickingId] = useState(null);

	const runSearch = async (q, page = 1) => {
		if (!q?.trim()) return;
		setLoading(true);
		try {
			const resp = await productImageSuggestServices.search({
				q,
				page,
			});
			const data = resp?.data ?? {};
			setResult((prev) => ({
				query: data.query,
				items: page === 1 ? data.items ?? [] : [...prev.items, ...(data.items ?? [])],
				page: data.page ?? page,
				pageCount: data.pageCount ?? 0,
				resultCount: data.resultCount ?? 0,
			}));
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: error,
			});
		} finally {
			setLoading(false);
		}
	};

	const handleOpen = () => {
		if (!productName?.trim()) {
			notificationServices.generateNotification({
				type: 'warning',
				message: 'Enter the product name first to get image suggestions',
			});
			return;
		}
		setQuery(productName);
		setResult(EMPTY_RESULT);
		setOpen(true);
		runSearch(productName, 1);
	};

	const handlePick = async (item) => {
		setPickingId(item.id);
		try {
			const resp = await productImageSuggestServices.fetchImage(item.id);
			const dataUrl = resp?.data?.dataUrl;
			if (!dataUrl) throw new Error('Image could not be loaded');
			const ext = (dataUrl.match(/data:image\/(\w+)/)?.[1] || 'jpg').replace('jpeg', 'jpg');
			onPick(dataUrlToFile(dataUrl, `suggested-${item.id}.${ext}`));
			setOpen(false);
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: error?.message ?? error,
			});
		} finally {
			setPickingId(null);
		}
	};

	if (isDisabled) return null;

	return (
		<>
			<button
				type='button'
				onClick={handleOpen}
				className='flex items-center justify-center gap-1 px-4 py-2 border border-blue-400 text-blue-500 text-sm rounded-md hover:bg-blue-50'
			>
				<ImageSearch fontSize='small' />
				Suggest images
			</button>
			<BaseModal
				open={open}
				heading='Suggested images'
				width='52rem'
				height='38rem'
				showClose
				hideFooter
				handleClose={() => setOpen(false)}
				aria={{
					label: 'suggested-images',
					description: 'suggested product images',
				}}
			>
				<div className='flex flex-col gap-3 h-full overflow-hidden'>
					<form
						className='flex gap-2'
						onSubmit={(e) => {
							e.preventDefault();
							runSearch(query, 1);
						}}
					>
						<input
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							className='flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm'
							placeholder='Search product name'
						/>
						<button type='submit' className='px-4 py-1.5 bg-blue-500 text-white text-sm rounded-md'>
							Search
						</button>
					</form>
					<p className='text-xs text-gray-500'>
						Images already set on similarly named products, closest names first.
						The picked image is copied to this product;
						the other product is not changed.
						{result.query ? ` Searched for "${result.query}".` : ''}
					</p>
					<div className='flex-1 overflow-auto'>
						{!loading && result.items.length === 0 && (
							<p className='text-center text-sm text-gray-500 py-10'>
								No similar products with images, try fewer or simpler words
							</p>
						)}
						<div className='grid grid-cols-4 gap-3'>
							{result.items.map((item) => (
								<button
									type='button'
									key={item.id}
									disabled={!!pickingId}
									onClick={() => handlePick(item)}
									title={item.title}
									className={`relative flex flex-col text-left border rounded-lg overflow-hidden hover:ring-2 hover:ring-blue-400 ${pickingId === item.id ? 'opacity-60' : ''}`}
								>
									<img src={item.thumbnail} alt={item.title || 'suggestion'} loading='lazy' className='w-full h-32 object-cover bg-gray-100' />
									{item.ownProduct && (
										<span className='absolute top-1 left-1 text-[10px] font-bold text-white px-1.5 py-0.5 rounded bg-green-600'>
											Your product
										</span>
									)}
									<span className='px-2 py-1 text-[11px] text-gray-600 truncate'>
										{pickingId === item.id ? 'Loading...' : item.title || '-'}
									</span>
								</button>
							))}
						</div>
						{loading && <p className='text-center text-sm text-gray-500 py-4'>Searching...</p>}
						{!loading && result.page > 0 && result.page < result.pageCount && (
							<div className='flex justify-center py-3'>
								<button
									type='button'
									onClick={() => runSearch(result.query || query, result.page + 1)}
									className='px-4 py-1.5 border border-gray-300 text-sm rounded-md hover:bg-gray-50'
								>
									Load more
								</button>
							</div>
						)}
					</div>
				</div>
			</BaseModal>
		</>
	);
};

export default ProductImageSuggestions;
